/**
 * «گفتگوها» — the AI Assistant conversation workspace (index route of
 * `/t/:slug/ai`).
 *
 * UI_REFERENCE_SOURCE:
 *   section: "Sakhtar AI Assistant.dc.html" — FRAME A (empty state, full
 *     shell), SECTION 04 (response components), FRAMES B · C · D (active
 *     conversation, second-layer drill-down with the context panel updated,
 *     cross-module analysis), FRAMES E–H (conversational states: E clarifying
 *     question, F partial access notice, G heavy analysis confirm, H
 *     analyzing) and FRAMES I · I-2 · J · K · L (blocking states: out of tokens
 *     with / without purchase permission, error with no tokens charged, tenant
 *     suspended, user not permitted) and the mobile frames M1–M4 (M5 is the wallet,
 *     built in `../wallet`).
 *   canonical pattern: three-column workspace (conversation rail · chat
 *     column · context panel) inside the app shell; fixed answer component
 *     order text → KPI row → chart → result table → drill chips → source card
 *     → anomaly → footer. E · G · H · I · J are assistant messages in the
 *     stream (`states/`), F is a notice band atop an answer, I also disables
 *     the composer, K · L replace the whole workspace (`states/WorkspaceBlocked`).
 *   desktop reference: Frame A (1440) — rail 280 / context panel 320; state
 *     cards 460 wide (I 560, K/L panel 460) as in the frames.
 *   mobile reference: M1 conversation list (own page), M2 conversation full screen
 *     (back + title + context button, composer pinned above the bottom nav), M3
 *     context bottom sheet, M4 out of tokens (warning bar, history «خواندنی»,
 *     disabled composer). Built for the shell's mobile range (≤ 899px; tablets
 *     included) as CSS media queries in `chat.css` / `states.css` over the BEM classes
 *     (`ai-rail`, `ai-chat`, `ai-panel`, `ai-answer`, `ai-state`, `ai-blocked`, …);
 *     JS only knows the page (`mobileView` → `data-mobile-view="list|chat"` on `.ai-chat`,
 *     the module shell hides its header / notice / tabs on `chat`) and the sheet
 *     (`mobile.ts`, `useModalSheet.ts`, `useMobileShell`).
 *   light/dark reference: design tokens only (`var(--sk-*)`, ChartKit theme).
 *   state reference: empty (A), active (B/C/D), clarify (E), partial access (F),
 *     confirm (G), analyzing (H), out of tokens (I/I-2), error (J), suspended
 *     (K), not permitted (L). Rules and precedence: `preflight.ts`.
 *   status: PARTIAL_REAL — the data scope badges / composer chips, the purchase
 *     permission (`canPurchase`, tenant.subscription.manage) and the partial
 *     access decision (F) are REAL (`useAiPreview()`, from the caller's own
 *     capabilities, DENY-first); everything else is KIT_CONFORMANT sample
 *     content. Suspension, user permission and service errors are decided by the
 *     backend in the product — here the «نمایش حالت» selector (`forcedState`)
 *     stands in for it.
 *
 * Documented deviations from the design:
 *   - Mobile (M1–M4): the composer keeps a text «ارسال» button (the design's chevron
 *     icon has no accessible name) and the scope chips + period select form a second,
 *     horizontally scrollable row under the input; the header scope badges are not shown
 *     on a phone (the composer's chips say the same, locked ones carry «بدون دسترسی»);
 *     the token-balance box of the rail is not repeated there (the module header's chip
 *     links to the wallet); drill chips scroll sideways instead of wrapping; result tables
 *     become one stacked card per row from the same markup (`data-label`).
 *   - The design's own sidebar + topbar are omitted — the real shell is used.
 *   - NO BACKEND: conversations, answers, figures and customer names are
 *     scripted samples (`sample.ts`) produced by a client-side demo engine
 *     (`engine.ts`); every answer carries the «نمونه» tag and the rail is
 *     labelled «نمونه — بدون داده واقعی». Tokens are only deducted from the
 *     preview wallet in `AiPreviewProvider`.
 *   - Controls whose backend does not exist are shown disabled, not faked:
 *     «خروجی» / «خروجی Excel» (export is centralised in the /data route) and
 *     «نمایش همه (N …)». «کپی» really copies; «درست بود / درست نبود» only
 *     toggle local state.
 *   - The suggested-prompt cards print a Persian category caption where the
 *     design prints its English `sk-frame-label` (an artboard caption, never
 *     product text).
 *   - The tenant's display name is not part of the shared preview context, so
 *     the subtitle says «سازمانتان» instead of «کیمیا».
 *   - Period list omits «بازهٔ دلخواه» (a Jalali range picker needs the real
 *     report backend); the period only labels the question — sample answers
 *     are fixed to شهریور ۱۴۰۵.
 *   - The header gear links to the assistant settings tab and is rendered for
 *     tenant admins only (the same condition as that tab).
 *   - Bar charts are vertical (ChartKit `BarChart`); the design's reference
 *     is horizontal.
 *   - Sample conversations / suggestions / drill targets needing a module the
 *     caller cannot read are hidden or inert, never answered.
 *   - Frames E–L deviations:
 *       · the designers' footnotes (F «اطلاع‌رسانی است، نه خطا…», I-2 «دکمهٔ خرید
 *         توکن … رندر نمی‌شود») are rules, not product copy, and are not printed;
 *       · the tenant name («کیمیا») and the role name («کارشناس انبار») are not in
 *         the shared preview context, so K/L say «سازمان شما» / «نقش شما»;
 *       · frame K's «تماس با پشتیبانی ساختار» has no endpoint or contact channel
 *         in this repo → rendered disabled; L links to the assistant settings
 *         for tenant admins only (the same condition as that tab);
 *       · K/L carry a preview-only «بازگشت به حالت عادی» (`setForcedState('none')`)
 *         because the selector's state would otherwise be unreachable;
 *       · H's step list names the modules being read instead of the design's
 *         invented record count («۱۴۲ سفارش فروش»), and its steps advance on a
 *         timer purely for show;
 *       · G's «heavy» rule models only what the demo can know — scripted answers
 *         flagged as row-by-row costing, or reading more than three modules; the
 *         period is only a label here, so «بازهٔ بیش از یک سال» cannot trigger it;
 *       · E's second option («بیشترین تعداد سفارش») continues with the same
 *         scripted sales-by-customer answer as the first (only that answer
 *         shows both measures); options that the caller's real scope could not
 *         answer are dropped, and with fewer than two the question is not asked;
 *       · F's answer for a partially-granted caller is a different scripted
 *         sample (`PARTIAL_FALLBACKS` in `engine.ts`) plus a sentence naming what
 *         was not computed, not the design's «۴٬۲۸۰٬۰۰۰٬۰۰۰ تومان» sentence;
 *       · I: besides the per-question block (balance below the estimate) the
 *         composer shows the design's standing disabled state when the balance
 *         cannot cover even the cheapest question (`MIN_QUESTION_TOKENS`);
 *       · J's cause line is generic (no «داده انبار»): there is no real cause.
 *   - Tokens are only deducted after an answer exists (`useChatSession.schedule`);
 *     every figure on the confirm / out-of-tokens cards is an integer ESTIMATE.
 *
 * Extending: see the header of `types.ts`, `preflight.ts` (single blocking gate,
 * precedence documented there) and `useChatSession.dispatch`; the composer accepts
 * `disabled` / `notice` / `placeholder` / `sendLabel` for blocking states.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAiPreview } from '../aiPreview.js';
import { useAiProvider } from '../connect/AiProviderContext.js';
import { groupDigits } from '../connect/display.ts';
import { ChatHeader } from './ChatHeader.js';
import { Composer } from './Composer.js';
import { ContextPanel } from './ContextPanel.js';
import { ConversationRail } from './ConversationRail.js';
import { EmptyState } from './EmptyState.js';
import { estimateTokens } from './engine.js';
import { MessageStream, messageDomId, scrollWithin } from './MessageStream.js';
import { useMobileShell } from '../useMobileShell.js';
import {
  exposedMobileView,
  scrollPageToTop,
  useVisualViewportKeyboard,
  type MobileView,
} from './mobile.js';
import { isOutOfTokens } from './preflight.js';
import { OutOfTokensBar, WorkspaceBlocked } from './states/index.js';
import type { PeriodId, ScopeKey, UserMessage } from './types.js';
import { useChatSession } from './useChatSession.js';
import './chat.css';
import './states.css';

export function ChatWorkspace() {
  const ai = useAiPreview();
  const provider = useAiProvider();
  const session = useChatSession();
  const [walletTokens, setWalletTokens] = useState<string | null>(null);
  const [reservedTokens, setReservedTokens] = useState<string | null>(null);
  useEffect(() => {
    if (provider.kind !== 'live') return;
    let cancelled = false;
    void provider
      .getBalance()
      .then((balance) => {
        if (cancelled) return;
        const availableTokens =
          balance.rawTokens?.available ??
          (balance.available.toman ? String(BigInt(balance.available.toman) * 5n) : null);
        const reservedTok =
          balance.rawTokens?.reserved ??
          (balance.reserved.toman ? String(BigInt(balance.reserved.toman) * 5n) : null);
        setWalletTokens(availableTokens);
        setReservedTokens(reservedTok);
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [provider, session.messages]);
  // Admin-only: has the assistant been switched on for this tenant? If not, the menu bubble pulses red.
  const [needsSetup, setNeedsSetup] = useState(false);
  useEffect(() => {
    if (provider.kind !== 'live' || !ai.isTenantAdmin) return;
    let cancelled = false;
    void provider
      .getActivation()
      .then((activation) => {
        if (!cancelled) setNeedsSetup(!(activation.active && activation.optedIn));
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [provider, ai.isTenantAdmin]);
  const rootRef = useRef<HTMLDivElement | null>(null);

  const [draft, setDraft] = useState('');
  const [period, setPeriod] = useState<PeriodId>('this-month');
  const [deepSearchEnabled, setDeepSearchEnabled] = useState(false);
  const [deepSearchScope, setDeepSearchScope] = useState<'web' | 'company' | 'hybrid'>('hybrid');
  const [optedOut, setOptedOut] = useState<readonly ScopeKey[]>([]);
  // The context panel is a power-user detail: closed until asked for, so the chat is the page.
  const [panelOpen, setPanelOpen] = useState(false);

  // Mobile flow (<= 899px, frames M1-M4): the list is its own page, a conversation opens full
  // screen, the context panel is a bottom sheet (closed by default). Desktop ignores all three.
  const isMobile = useMobileShell();
  // On a phone the conversation is always the page; the history is a drawer from the left.
  const [mobileView, setMobileView] = useState<MobileView>('chat');
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [sheetOpen, setSheetOpen] = useState(false);
  const view = exposedMobileView(isMobile, mobileView);
  const closeSheet = useCallback(() => setSheetOpen(false), []);
  const isKeyboardOpen = useVisualViewportKeyboard(isMobile, view);

  useEffect(() => {
    if (isKeyboardOpen) {
      const scroller = rootRef.current?.querySelector<HTMLElement>('.ai-chat__body');
      if (scroller) {
        requestAnimationFrame(() => {
          scroller.scrollTop = scroller.scrollHeight;
        });
      }
    }
  }, [isKeyboardOpen]);

  const granted = useMemo(
    () => ai.scope.filter((item) => item.granted).map((item) => item.key),
    [ai.scope],
  );
  // Selected = really granted AND not switched off. A module without access can never be selected.
  const selected = useMemo(
    () => granted.filter((key) => !optedOut.includes(key)),
    [granted, optedOut],
  );
  const estimate = estimateTokens(draft, selected.length);

  const toggleScope = useCallback((key: ScopeKey) => {
    setOptedOut((current) =>
      current.includes(key) ? current.filter((k) => k !== key) : [...current, key],
    );
  }, []);

  const openConversation = useCallback(
    (id: string) => {
      session.select(id);
      setMobileView('chat');
      setDrawerOpen(false);
    },
    [session],
  );

  const startConversation = useCallback(() => {
    session.newChat();
    setMobileView('chat');
    setDrawerOpen(false);
  }, [session]);

  const backToList = useCallback(() => {
    setSheetOpen(false);
    setDrawerOpen(true);
  }, []);
  const closeDrawer = useCallback(() => setDrawerOpen(false), []);

  useEffect(() => {
    if (!isMobile || !drawerOpen) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setDrawerOpen(false);
    };
    document.addEventListener('keydown', onKey);
    rootRef.current?.querySelector<HTMLElement>('.ai-rail__new')?.focus({ preventScroll: true });
    return () => document.removeEventListener('keydown', onKey);
  }, [isMobile, drawerOpen]);

  // A page change moves focus to the new page (the button that was tapped is gone from the screen)
  // and starts it at the top; the very first render leaves focus alone.
  const lastView = useRef<MobileView | null>(null);
  useEffect(() => {
    const previous = lastView.current;
    lastView.current = view;
    if (!isMobile || previous === null || previous === view) return;
    const root = rootRef.current;
    if (!root) return;
    scrollPageToTop(root);
    const target =
      view === 'chat'
        ? root.querySelector<HTMLElement>('.ai-chat__back')
        : (root.querySelector<HTMLElement>(
            '.ai-rail__item[data-active="true"] .ai-rail__item-main',
          ) ?? root.querySelector<HTMLElement>('.ai-rail__new'));
    target?.focus({ preventScroll: true });
  }, [view, isMobile]);

  const submitDraft = useCallback(() => {
    if (session.isThinking) return;
    session.send(draft, {
      selected,
      period,
      ...(deepSearchEnabled ? { deepSearch: { enabled: true, scope: deepSearchScope } } : {}),
    });
    setDraft('');
  }, [session, draft, selected, period, deepSearchEnabled, deepSearchScope]);

  const pickSuggestion = useCallback(
    (text: string) => {
      setMobileView('chat');
      session.send(text, { selected: granted, period });
    },
    [session, granted, period],
  );

  const handlePeriodChange = useCallback(
    (newPeriod: PeriodId) => {
      setPeriod(newPeriod);
      if (session.messages.length > 0 && !session.isThinking) {
        const userPrompts = session.messages.filter(
          (m): m is UserMessage => m.role === 'user' && m.kind === 'prompt',
        );
        const lastPrompt = userPrompts[userPrompts.length - 1];
        if (lastPrompt) {
          session.send(lastPrompt.text, {
            selected: lastPrompt.scope.length > 0 ? lastPrompt.scope : selected,
            period: newPeriod,
          });
        }
      }
    },
    [session, selected],
  );

  const jumpTo = useCallback((messageId: string) => {
    setSheetOpen(false);
    const target = document.getElementById(messageDomId(messageId));
    const scroller = rootRef.current?.querySelector<HTMLElement>('.ai-chat__body');
    if (target && scroller) scrollWithin(scroller, target, 'smooth');
  }, []);

  const isEmpty = session.messages.length === 0;

  // The greeting of a new conversation starts at its top, not wherever the previous thread was scrolled.
  useEffect(() => {
    if (!isEmpty) return;
    const scroller = rootRef.current?.querySelector<HTMLElement>('.ai-chat__body');
    if (scroller) scroller.scrollTop = 0;
  }, [isEmpty, session.activeId]);

  // Frames L / K replace the whole workspace (precedence: not-permitted > suspended, as in preflight).
  if (ai.forcedState === 'not-permitted') return <WorkspaceBlocked reason="not-permitted" />;
  if (ai.forcedState === 'suspended') return <WorkspaceBlocked reason="suspended" />;

  // Frame I: with less than the cheapest question's estimate nothing can be sent at all.
  const noTokens = isOutOfTokens(ai.tokens.balance);
  // A phone's one-line composer cannot hold the desktop's long example question.
  const composerPlaceholder = noTokens
    ? isMobile
      ? 'موجودی توکن لازم است'
      : 'برای ارسال پرسش، موجودی توکن لازم است'
    : isMobile
      ? isEmpty
        ? 'پرسش خود را بنویسید…'
        : 'پرسش بعدی…'
      : provider.kind === 'live'
        ? 'سؤال خود را بپرسید؛ برای گزارش شرکت، موضوع و بازهٔ زمانی را بنویسید.'
        : undefined;

  return (
    <div
      ref={rootRef}
      className="sk-card sk-card--clipped ai-chat"
      data-panel-open={panelOpen}
      data-mobile-view={view}
      data-drawer={isMobile && drawerOpen ? 'open' : 'closed'}
    >
      {isMobile ? (
        <button
          type="button"
          className="ai-drawer-scrim"
          tabIndex={-1}
          aria-label="بستن تاریخچه"
          onClick={closeDrawer}
        />
      ) : null}
      <ConversationRail
        conversations={session.conversations}
        activeId={session.activeId}
        onSelect={openConversation}
        onNew={startConversation}
        onTogglePin={session.togglePin}
        onRemove={session.remove}
        tokens={
          provider.kind === 'live' && walletTokens !== null
            ? { balance: Number(walletTokens), purchased: Number(walletTokens) }
            : ai.tokens
        }
        {...(provider.kind === 'live'
          ? {
              balanceLabel: 'مانده توکن',
              balanceHint: (
                <>
                  رزروشده{' '}
                  <span className="sk-num">
                    {reservedTokens === null ? '—' : groupDigits(reservedTokens)}
                  </span>{' '}
                  توکن
                </>
              ),
            }
          : {})}
        walletHref={`${ai.base}/ai/wallet`}
        readOnly={noTokens}
        isLive={provider.kind === 'live'}
        menuBase={ai.base}
        isTenantAdmin={ai.isTenantAdmin}
        needsSetup={needsSetup}
        notice={
          isMobile && noTokens ? (
            <OutOfTokensBar
              balance={ai.tokens.balance}
              canPurchase={ai.canPurchase}
              walletHref={`${ai.base}/ai/wallet`}
            />
          ) : undefined
        }
      />

      <section className="ai-chat__main" aria-label="گفتگو">
        <ChatHeader
          title={session.activeTitle}
          scope={ai.scope}
          settingsHref={ai.isTenantAdmin ? `${ai.base}/ai/settings` : undefined}
          statusBadge={
            noTokens ? (ai.tokens.balance === 0 ? 'موجودی صفر' : 'موجودی کافی نیست') : undefined
          }
          onBack={backToList}
          onOpenContext={() => setSheetOpen(true)}
          contextOpen={sheetOpen}
        />
        <div className="ai-chat__body">
          {isEmpty ? (
            <EmptyState
              firstName={ai.firstName}
              live={provider.kind === 'live'}
              granted={granted}
              onPick={pickSuggestion}
              disabled={session.isThinking || noTokens}
            />
          ) : (
            <MessageStream
              messages={session.messages}
              lastAnswerId={session.lastAnswerId}
              onDrill={session.drill}
              onFocusRecord={session.focusRecord}
              stateActions={{
                onChooseOption: session.chooseOption,
                onConfirmHeavy: session.confirmHeavy,
                onCancelHeavy: session.cancelHeavy,
                onStop: session.stop,
                onRetry: session.retry,
                onEditPrompt: setDraft,
              }}
              walletHref={`${ai.base}/ai/wallet`}
              conversationKey={session.activeId}
              userName={ai.fullName}
            />
          )}
        </div>
        <Composer
          value={draft}
          onChange={setDraft}
          onSubmit={submitDraft}
          scope={ai.scope}
          selected={selected}
          onToggleScope={toggleScope}
          period={period}
          onPeriodChange={handlePeriodChange}
          estimate={estimate}
          allowGeneral={provider.kind === 'live'}
          deepSearchEnabled={deepSearchEnabled}
          onToggleDeepSearch={() => setDeepSearchEnabled((prev) => !prev)}
          deepSearchScope={deepSearchScope}
          onDeepSearchScopeChange={setDeepSearchScope}
          busy={session.isThinking}
          disabled={noTokens}
          placeholder={composerPlaceholder}
          notice={
            noTokens ? (
              <OutOfTokensBar
                balance={ai.tokens.balance}
                canPurchase={ai.canPurchase}
                walletHref={`${ai.base}/ai/wallet`}
              />
            ) : undefined
          }
        />
      </section>

      {isMobile ? (
        <ContextPanel
          sheet
          context={session.context}
          open={sheetOpen && view === 'chat'}
          onToggle={closeSheet}
          onJumpTo={jumpTo}
          base={ai.base}
        />
      ) : (
        <ContextPanel
          context={session.context}
          open={panelOpen}
          onToggle={() => setPanelOpen((value) => !value)}
          onJumpTo={jumpTo}
          base={ai.base}
        />
      )}
    </div>
  );
}
